import { memo, useCallback, useRef, useMemo } from 'react';
import './SideRail.css';

// Re-uses the same nav-item config as BottomNav. Keep in sync if you change
// BottomNav.primaryNavItems / secondaryNavItems.
const primaryNavItems = [
  { id: 'map', label: 'Live', icon: '/icons/map.png' },
  { id: 'receivers', label: 'Receivers', icon: '/icons/location.png' },
  { id: 'history', label: 'History', icon: '/icons/history.svg' },
  // Predictions is desktop-only: not offered on tablets/iPads. The Airport
  // board is its tablet/phone counterpart (same flights, status only).
  { id: 'airport', label: 'Airport', icon: '/icons/airport_white.svg' },
  { id: 'messages', label: 'Messages', icon: '/icons/messages.svg' },
  { id: 'airband', label: 'Airband', icon: '/icons/messages.svg' },
  { id: 'statistics', label: 'Statistics', icon: '/icons/history.svg' },
  { id: 'notams', label: 'NOTAMs', icon: '/icons/notam.svg' },
];

const utilityNavItems = [
  { id: 'account', label: 'Account', icon: '/icons/person.png' },
  { id: 'settings', label: 'Settings', icon: '/icons/settings.svg' },
];

/**
 * Vertical rail navigation for tablet (600–1023 px). Replaces the BottomNav
 * at this breakpoint — wider screens than phone justify a sidebar that's
 * always visible, narrower than desktop don't justify the full top-bar Nav.
 *
 * 64 px wide, icon + label stacked. Same touch-target rules as BottomNav.
 */
function SideRail({ currentPage, onPageChange, isViewerOnly = false }) {
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;

  const handleClick = useCallback((id) => {
    if (onPageChangeRef.current) onPageChangeRef.current(id);
  }, []);

  const filteredPrimary = useMemo(() => {
    if (isViewerOnly) {
      return primaryNavItems.filter(item => item.id !== 'receivers');
    }
    return primaryNavItems;
  }, [isViewerOnly]);

  const filteredUtility = useMemo(() => {
    if (isViewerOnly) {
      return utilityNavItems.filter(item => item.id !== 'account');
    }
    return utilityNavItems;
  }, [isViewerOnly]);

  return (
    <nav className="side-rail" aria-label="Primary">
      <div className="side-rail-section">
        {filteredPrimary.map(item => (
          <button
            key={item.id}
            className={`side-rail-item ${currentPage === item.id ? 'active' : ''}`}
            onClick={() => handleClick(item.id)}
            onTouchEnd={(e) => { e.preventDefault(); handleClick(item.id); }}
            aria-current={currentPage === item.id ? 'page' : undefined}
          >
            <img src={item.icon} alt="" className="side-rail-icon" />
            <span className="side-rail-label">{item.label}</span>
          </button>
        ))}
      </div>

      <div className="side-rail-spacer" />

      <div className="side-rail-section">
        {filteredUtility.map(item => (
          <button
            key={item.id}
            className={`side-rail-item ${currentPage === item.id ? 'active' : ''}`}
            onClick={() => handleClick(item.id)}
            onTouchEnd={(e) => { e.preventDefault(); handleClick(item.id); }}
          >
            <img src={item.icon} alt="" className="side-rail-icon" />
            <span className="side-rail-label">{item.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}

export default memo(SideRail);
